import React from 'react';
import {
  NodeContent,
  NodeDescription,
  NodeFooter,
  NodeHeader,
  NodeTitle,
} from '@cotera/client/v0/components/ai-elements/node';
import { Badge } from '@cotera/client/v0/components/ui/badge';
import { Card } from '@cotera/client/v0/components/ui/card';
import { cn } from '@cotera/client/v0/lib/utils';
import { Position } from '@xyflow/react';
import { WORKFLOW_FLOW_NODE_WIDTH } from '../../workflow-flow-layout.constants';
import { WorkflowHandle } from '../workflow-handle';
import type { WorkflowNodeHandles } from './types';
import { NODE_ACCENTS, type NodeAccent } from '../node-accent';

export type WorkflowNodeShellProps = {
  title: string;
  description?: string;
  icon?: React.ReactNode;
  accent?: NodeAccent;
  badge?: string;
  handles?: WorkflowNodeHandles;
  footer?: React.ReactNode;
  children?: React.ReactNode;
  className?: string;
};

const DEFAULT_HANDLES: WorkflowNodeHandles = { target: true, source: true };

/**
 * Shared chrome for all workflow graph nodes. Type-specific content is supplied
 * by a {@link WorkflowNodeRenderStrategy}.
 */
export const WorkflowNodeShell: React.FC<WorkflowNodeShellProps> = ({
  title,
  description,
  icon,
  accent = 'neutral',
  badge,
  handles = DEFAULT_HANDLES,
  footer,
  children,
  className,
}) => {
  const hasBody = children !== undefined && children !== null;
  const hasFooter = footer !== undefined && footer !== null;
  const accentStyle = NODE_ACCENTS[accent];

  return (
    <Card
      className={cn(
        'node-container relative h-auto gap-0 rounded-2xl p-0 shadow-xs transition-[color,box-shadow,border-color]',
        className
      )}
      style={{ width: WORKFLOW_FLOW_NODE_WIDTH }}
    >
      {handles.target ? (
        <WorkflowHandle position={Position.Top} type="target" />
      ) : null}
      {handles.source ? (
        <WorkflowHandle position={Position.Bottom} type="source" />
      ) : null}
      {/* One flat card surface: the header/footer primitives tint themselves
          `bg-secondary` and rule themselves off with `border-b`/`border-t` by
          default. Both come off the header so the node reads as a single
          uninterrupted colour, with the icon chip carrying the accent instead. */}
      <NodeHeader className="p-3! rounded-t-2xl bg-transparent border-b-0">
        {/* `items-center` centres the icon chip against the whole text block, so
            on a two-line header it sits across the title *and* the description
            rather than only the title — the chip stands for the node, and the
            description is a subtitle of the same thing. Safe to centre because
            both lines `truncate`, so the block is always exactly one or two
            lines and the chip can never float mid-paragraph. */}
        <div className="flex items-center gap-2.5 min-w-0 w-full">
          {/* 36px chip against the 44px two-line text block (28px title band +
              16px description), so it spans both lines instead of sitting against
              the title alone. Strategies all hand us a 16px `h-4 w-4` glyph, which
              would look lost at this size — `[&_svg]:size-5` scales it here rather
              than editing every strategy, and wins on specificity over the icon's
              own class. Keeps roughly the original glyph-to-chip ratio. */}
          {icon !== undefined && icon !== null ? (
            <div
              className={cn(
                'shrink-0 flex h-9 w-9 items-center justify-center rounded-lg [&_svg]:size-5',
                accentStyle.chip
              )}
            >
              {icon}
            </div>
          ) : null}
          <div className="min-w-0 flex-1">
            {/* Alignment comes from the row's `items-center`, not from matching
                heights. `leading-7` is here for rhythm: it gives the title a 28px
                band, which sets the two-line block to a round 44px and supplies
                the gap down to the description without a margin. */}
            <NodeTitle className="text-sm font-medium leading-7 truncate">
              {title}
            </NodeTitle>
            {/* No top margin — the title's taller line box already supplies the
                gap below the text. */}
            {description !== undefined && description !== '' ? (
              <NodeDescription className="text-xs truncate">
                {description}
              </NodeDescription>
            ) : null}
          </div>
          {/* `self-start` opts the badge out of the row's centring, and the 28px
              band then lines it up with the title. Centred it would drift into the
              gap between the two lines; a label belongs on the node's name. */}
          {badge !== undefined && badge !== '' ? (
            <div className="flex h-7 shrink-0 items-center self-start">
              <Badge variant={accentStyle.badge} size="sm">
                {badge}
              </Badge>
            </div>
          ) : null}
        </div>
      </NodeHeader>
      {hasBody ? <NodeContent>{children}</NodeContent> : null}
      {hasFooter ? (
        <NodeFooter className="rounded-b-2xl bg-transparent">
          {footer}
        </NodeFooter>
      ) : null}
    </Card>
  );
};
